import { ref } from "vue";
import axios from "axios";
import { useStore } from "vuex";
import useTracking from "@/composables/usePropertyTracking";
import useAuth from "@/composables/useAuth";
import { useRouter } from "vue-router";

export function useProperties() {
  const store = useStore();
  const base_url = store.state.baseUrl;
  const hoverStartTime = ref(null);
  const hoverDurations = ref({});
  const propertiesBeforeProjects = ref([]);
  const properties = ref([]);
  const propertiesAfterProjects = ref([]);
  const propertiesCount = ref(0);
  const propertyTypes = ref({});
  const projects = ref([]);
  const title = ref("");
  const buttonLabel = ref("");
  const track = useTracking();
  const { state } = useAuth();
  const router = useRouter();

  const fetchPropertyData = async (routeName) => {
    let tenure_name = "";
    if (routeName === "properties-for-rent") {
      tenure_name = "rent";
      title.value = "Properties for rent in Ghana";
      buttonLabel.value = "Rent";
    } else if (routeName === "properties-for-sale") {
      tenure_name = "buy";
      title.value = "Properties for sale in Ghana";
      buttonLabel.value = "Buy";
    } else if (routeName === "properties-for-short-stay") {
      tenure_name = "short-stay";
      title.value = "Short stay accommodation in Ghana";
      buttonLabel.value = "Short Stay";
    } else if (routeName === "commercial-properties") {
      tenure_name = "commercial";
      title.value = "Commercial properties for rent in Ghana";
      buttonLabel.value = "Commercial Rent";
    }

    try {
      const response = await axios.get(
        `${base_url}properties-by-tenure/${tenure_name}`
      );
      propertiesBeforeProjects.value = response.data.data.slice(0, 5);
      propertiesAfterProjects.value = response.data.data.slice(5, 30);
      propertiesCount.value = response.data.data.length;
    } catch (error) {
      console.error("Error fetching properties:", error.message);
    }

    try {
      const propertyTypesResponse = await axios.get(
        `${base_url}property-types/${tenure_name}`
      );
      propertyTypes.value = propertyTypesResponse.data;
    } catch (error) {
      console.error("Error fetching property types:", error.message);
    }
  };

  const getProjects = async () => {
    try {
      const response = await axios.get(`${base_url}new-projects`);
      projects.value = response.data.data.slice(0, 8);
    } catch (error) {
      console.error("Error fetching projects:", error.message);
    }
  };

  const handlePropertyClick = async (property, routeName) => {
    const userId = state.user?.id;
    const conversionType = "click";

    const metrics = {
      device_type: getDeviceType(),
      geo_location: await getUserLocation(),
    };

    const routeData = router.resolve({
      name: "PropertyView",
      params: {
        id: property.id,
        slug: sanitizeTitle(property.title),
      },
    });
    router.push(routeData.href);

    track
      .incrementPropertyClicks(property.id, metrics)
      .then(() => track.recordConversion(property.id, userId, conversionType))
      .then(() => track.recordPageView(property.id, routeName))
      .catch((error) => console.error("Error tracking ad click:", error));
  };

  const getDeviceType = () => {
    const userAgent = navigator.userAgent;
    if (/android/i.test(userAgent)) {
      return "Android";
    } else if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      return "iOS";
    } else if (/Macintosh/i.test(userAgent)) {
      return "MacOS";
    } else if (/Windows/i.test(userAgent)) {
      return "Windows";
    } else {
      return "Unknown";
    }
  };

  const getUserLocation = async () => {
    try {
      const response = await axios.get("https://ipapi.co/json/");
      return `${response.data.city}, ${response.data.country_name}`;
    } catch (error) {
      console.error("Error fetching user location:", error.message);
      return "Unknown";
    }
  };

  const handlePropertyViews = async (propertyId) => {
    const metrics = {
      device_type: getDeviceType(),
      geo_location: await getUserLocation(),
    };
    track.incrementPropertyViews(propertyId, metrics);
  };

  const handleAdHoverStart = async () => {
    hoverStartTime.value = new Date();
  };

  const handleAdHoverEnd = async (propertyId) => {
    if (hoverStartTime.value) {
      const hoverEndTime = new Date();
      const hoverDuration = (hoverEndTime - hoverStartTime.value) / 1000;

      if (!hoverDurations.value[propertyId]) {
        hoverDurations.value[propertyId] = [];
      }
      hoverDurations.value[propertyId].push(hoverDuration);

      const avg_time_spent = calculateAvgTimeOnAd(propertyId);
      // const hovers = hoverDurations.value[propertyId].length;

      await track.incrementPropertyViews(propertyId, avg_time_spent);

      hoverStartTime.value = null;
    }
  };

  const calculateAvgTimeOnAd = (adId) => {
    const durations = hoverDurations.value[adId];
    if (!durations || durations.length === 0) return 0;

    const totalDuration = durations.reduce(
      (sum, duration) => sum + duration,
      0
    );
    return totalDuration / durations.length;
  };

  const trackContactRequest = (propertyId) => {
    track.incrementMetric(propertyId, "contact_requests");
  };

  const trackShare = (propertyId) => {
    track.incrementMetric(propertyId, "shares");
  };

  const trackFavorite = (propertyId) => {
    track.incrementMetric(propertyId, "favorites");
  };

  const trackScrollDepth = (propertyId) => {
    const scrollDepth = window.scrollY + window.innerHeight;
    track.updateMetric(propertyId, "scroll_depth", scrollDepth);
  };

  const sanitizeTitle = (title) => {
    return title.replace(/[^a-z0-9]/gi, "-").toLowerCase();
  };

  return {
    handleAdHoverStart,
    handleAdHoverEnd,
    trackContactRequest,
    trackShare,
    trackFavorite,
    trackScrollDepth,
    fetchPropertyData,
    properties,
    propertiesBeforeProjects,
    propertiesAfterProjects,
    propertiesCount,
    propertyTypes,
    title,
    buttonLabel,
    projects,
    getProjects,
    handlePropertyClick,
    handlePropertyViews,
  };
}
